
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Tabs,
    Tab,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Button,
    TextField,
    InputAdornment,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    Chip,
    CircularProgress,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Link as MuiLink
} from '@mui/material';
import {
    Info as InfoIcon,
    CheckCircle as SuccessIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    DeleteSweep as DeleteAllIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    DoneAll as MarkAllReadIcon,
    Clear as ClearIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useNotifications } from '../context/NotificationContext';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import EmptyState from '../components/common/EmptyState';
import PageHeader from '../components/common/PageHeader';
import notificationService from '../services/notificationService';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/**
 * Page de gestion des notifications
 */
const NotificationsPage = () => {
    const { markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications } = useNotifications();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        type: '',
        isRead: ''
    });
    // Ajout d'un état pour suivre les compteurs de notifications
    const [counters, setCounters] = useState({
        total: 0,
        unread: 0,
        read: 0
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Fonction pour gérer les clics sur les liens
    const handleLinkClick = (e, link) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }

        if (!link) {
            console.warn('Tentative de clic sur un lien vide ou invalide');
            return;
        }

        console.log('Ouverture du lien:', link); // Ajouter pour le débogage

        try {
            if (link.startsWith('http://') || link.startsWith('https://')) {
                window.open(link, '_blank');
            } else {
                navigate(link);
            }
        } catch (error) {
            console.error('Erreur lors de l\'ouverture du lien:', error);
            console.error('Lien problématique:', link);
        }
    };

    /**
     * Charger les notifications avec pagination et filtres
     */
    const loadNotifications = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Déterminer les filtres en fonction de l'onglet actif
            let isReadFilter;

            if (activeTab === 1) {
                isReadFilter = 'false'; // Non lues
            } else if (activeTab === 2) {
                isReadFilter = 'true'; // Lues
            } else {
                // Pour l'onglet "Toutes"
                isReadFilter = filters.isRead || undefined; // Utiliser undefined si vide
            }

            // Charger les notifications filtrées
            const result = await notificationService.getNotifications({
                page,
                limit: 10,
                type: filters.type,
                isRead: isReadFilter,
                search
            });

            setNotifications(result.notifications || []);
            setTotalPages(result.totalPages || 1);
            setTotalCount(result.totalCount || 0);
            
            // Utiliser directement les compteurs fournis par le backend
            setCounters({
                total: result.totalCount || 0,
                unread: result.unreadCount || 0,
                read: result.readCount || 0
            });
        } catch (err) {
            console.error('Error loading notifications:', err);
            setError('Impossible de charger les notifications');
        } finally {
            setLoading(false);
        }
    }, [activeTab, filters, page, search]);

    // Créer une version debounced de loadNotifications
    const debouncedSearch = useCallback(
        debounce(() => {
            loadNotifications();
        }, 500),
        [loadNotifications]
    );

    // Nettoyer la fonction debounce lors du démontage
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Un seul useEffect pour charger les notifications
    useEffect(() => {
        loadNotifications();
    }, [loadNotifications]);

    useEffect(() => {
        notifications.forEach(notification => {
            console.log(`Notification ${notification.id} - link:`, notification.link);
        });
    }, [notifications]);

    // Gestionnaire de recherche pour la soumission du formulaire
    const handleSearch = (event) => {
        if (event) {
            event.preventDefault();
        }
        debouncedSearch();
    };

    // Gestionnaire de changement de recherche
    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        debouncedSearch();
    };

    /**
     * Changer d'onglet
     */
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(1);

        // Réinitialiser le filtre isRead lorsqu'on passe à l'onglet "Toutes"
        if (newValue === 0) {
            setFilters(prev => ({ ...prev, isRead: '' }));
        }
    };

    /**
     * Changer de page
     */
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    /**
     * Mettre à jour les filtres
     */
    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPage(1);
    };

    /**
     * Réinitialiser les filtres
     */
    const handleResetFilters = () => {
        setFilters({
            type: '',
            isRead: ''
        });
        setSearch('');
        setPage(1);

        // Déclencher immédiatement la recherche après réinitialisation
        loadNotifications();
    };

    /**
     * Marquer une notification comme lue
     */
    const handleMarkAsRead = async (notificationId) => {
        try {
            await markAsRead(notificationId);

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.map(notification =>
                    notification.id === notificationId
                        ? { ...notification, isRead: true }
                        : notification
                )
            );
            
            // Mettre à jour les compteurs
            setCounters(prev => ({
                ...prev,
                unread: Math.max(0, prev.unread - 1),
                read: prev.read + 1
            }));
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    /**
     * Gérer le clic sur une notification
     */
    const handleNotificationClick = async (notification) => {
        console.log('Notification clicked:', {
            id: notification.id,
            link: notification.link,
            title: notification.title
        });

        // Marquer comme lue si pas déjà lu
        if (!notification.isRead) {
            await handleMarkAsRead(notification.id);
        }

        // Ouvrir le lien dans un nouvel onglet si disponible
        if (notification.link) {
            window.open(notification.link, '_blank');
        }
    };

    /**
     * Marquer toutes les notifications comme lues
     */
    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead();

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.map(notification => ({ ...notification, isRead: true }))
            );
            
            // Mettre à jour les compteurs
            setCounters(prev => ({
                ...prev,
                read: prev.total,
                unread: 0
            }));
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        }
    };

    /**
     * Ouvrir la boîte de dialogue de suppression
     */
    const handleOpenDeleteDialog = (notification) => {
        setSelectedNotification(notification);
        setDeleteDialogOpen(true);
    };

    /**
     * Fermer la boîte de dialogue de suppression
     */
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setSelectedNotification(null);
    };

    /**
     * Ouvrir la boîte de dialogue de suppression de toutes les notifications
     */
    const handleOpenDeleteAllDialog = () => {
        setDeleteAllDialogOpen(true);
    };

    /**
     * Fermer la boîte de dialogue de suppression de toutes les notifications
     */
    const handleCloseDeleteAllDialog = () => {
        setDeleteAllDialogOpen(false);
    };

    /**
     * Supprimer une notification
     */
    const handleDeleteNotification = async () => {
        if (!selectedNotification) return;

        try {
            await deleteNotification(selectedNotification.id);

            // Mettre à jour l'état local
            setNotifications(prev =>
                prev.filter(notification => notification.id !== selectedNotification.id)
            );
            
            // Mettre à jour les compteurs
            setCounters(prev => {
                const isRead = selectedNotification.isRead;
                return {
                    total: Math.max(0, prev.total - 1),
                    unread: isRead ? prev.unread : Math.max(0, prev.unread - 1),
                    read: isRead ? Math.max(0, prev.read - 1) : prev.read
                };
            });

            handleCloseDeleteDialog();
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    /**
     * Supprimer toutes les notifications
     */
    const handleDeleteAllNotifications = async () => {
        try {
            await deleteAllNotifications();

            // Mettre à jour l'état local
            setNotifications([]);
            setTotalCount(0);
            
            // Réinitialiser les compteurs
            setCounters({
                total: 0,
                unread: 0,
                read: 0
            });

            handleCloseDeleteAllDialog();
        } catch (err) {
            console.error('Error deleting all notifications:', err);
        }
    };

    /**
     * Obtenir l'icône en fonction du type de notification
     */
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <SuccessIcon sx={{ color: 'success.main' }} />;
            case 'warning':
                return <WarningIcon sx={{ color: 'warning.main' }} />;
            case 'error':
                return <ErrorIcon sx={{ color: 'error.main' }} />;
            case 'info':
            default:
                return <InfoIcon sx={{ color: 'info.main' }} />;
        }
    };

    /**
     * Obtenir la couleur de fond en fonction du type de notification
     */
    const getNotificationColor = (type) => {
        switch (type) {
            case 'success':
                return 'success.light';
            case 'warning':
                return 'warning.light';
            case 'error':
                return 'error.light';
            case 'info':
            default:
                return 'info.light';
        }
    };

    /**
     * Formater la date relative
     */
    const formatRelativeTime = (date) => {
        try {
            return formatDistanceToNow(new Date(date), { addSuffix: true, locale: fr });
        } catch (error) {
            return 'Date inconnue';
        }
    };

    /**
     * Formater la date complète
     */
    const formatFullDate = (date) => {
        try {
            return format(new Date(date), 'PPPp', { locale: fr });
        } catch (error) {
            return 'Date inconnue';
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-start',
        mb: 2,
        py: { xs: 1.5, md: 2 },
        px: { xs: 2, md: 3 },
        borderRadius: 2,
        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
        color: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5, color: 'white', fontSize: { xs: '1.3rem', sm: '1.5rem', md: '2rem' } }}>
          {t('notifications.notifications')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          {t('notifications.managePreferences')}
        </Typography>
      </Box>
      <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 2 }}>
                            <Tabs value={activeTab} onChange={handleTabChange} aria-label={t('notifications.tabs.ariaLabel')} variant="scrollable" scrollButtons="auto">
                                <Tab 
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {t('notifications.tabs.all')}
                                            <Chip
                                                size="small"
                                                label={counters.total}
                                                color="default"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    }
                                    id="tab-0" 
                                    aria-controls="tabpanel-0" 
                                />
                                <Tab
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {t('notifications.tabs.unread')}
                                            <Chip
                                                size="small"
                                                label={counters.unread}
                                                color="error"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    }
                                    id="tab-1"
                                    aria-controls="tabpanel-1"
                                />
                                <Tab
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {t('notifications.tabs.read')}
                                            <Chip
                                                size="small"
                                                label={counters.read}
                                                color="success"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    }
                                    id="tab-2"
                                    aria-controls="tabpanel-2"
                                />
                            </Tabs>
                            <Box sx={{ display: 'flex', alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                                {counters.unread > 0 && (
                                    <Tooltip title={t('notifications.markAllAsRead')}>
                                        <IconButton
                                            color="primary"
                                            onClick={handleMarkAllAsRead}
                                            aria-label={t('notifications.markAllAsRead')}
                                        >
                                            <MarkAllReadIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {counters.total > 0 && (
                                    <Tooltip title={t('notifications.deleteAll.title')}>
                                        <IconButton
                                            color="error"
                                            onClick={handleOpenDeleteAllDialog}
                                            aria-label={t('notifications.deleteAll.title')}
                                        >
                                            <DeleteAllIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ mb: 3 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={4}>
                                    <form onSubmit={handleSearch}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder={t('common.search')}
                                            value={search}
                                            onChange={handleSearchChange}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: search && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSearch('');
                                                                setTimeout(() => loadNotifications(), 0);
                                                            }}
                                                            aria-label={t('common.clearSearch')}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </form>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel id="type-filter-label">{t('notifications.filters.type', 'Type')}</InputLabel>
                                        <Select
                                            labelId="type-filter-label"
                                            id="type-filter"
                                            name="type"
                                            value={filters.type}
                                            onChange={handleFilterChange}
                                            label={t('notifications.filters.type', 'Type')}
                                        >
                                            <MenuItem value="">{t('common.all')}</MenuItem>
                                            <MenuItem value="info">{t('common.info')}</MenuItem>
                                            <MenuItem value="success">{t('common.success')}</MenuItem>
                                            <MenuItem value="warning">{t('common.warning')}</MenuItem>
                                            <MenuItem value="error">{t('common.error')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {activeTab === 0 && (
                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth variant="outlined">
                                            <InputLabel id="read-filter-label">{t('notifications.filters.status', 'Statut')}</InputLabel>
                                            <Select
                                                labelId="read-filter-label"
                                                id="read-filter"
                                                name="isRead"
                                                value={filters.isRead}
                                                onChange={handleFilterChange}
                                                label={t('notifications.filters.status', 'Statut')}
                                            >
                                                <MenuItem value="">{t('common.all')}</MenuItem>
                                                <MenuItem value="false">{t('notifications.status.unread', 'Non lu')}</MenuItem>
                                                <MenuItem value="true">{t('notifications.status.read', 'Lu')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<FilterIcon />}
                                        onClick={handleResetFilters}
                                    >
                                        {t('common.reset')}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box sx={{ minHeight: 400 }}>
                            {loading ? (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 400
                                }}>
                                    <CircularProgress />
                                </Box>
                            ) : error ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography color="error">{error}</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ mt: 2 }}
                                        onClick={loadNotifications}
                                    >
                                        {t('common.tryAgain')}
                                    </Button>
                                </Box>
                            ) : notifications.length === 0 ? (
                                <EmptyState
                                    icon={<NotificationsIcon sx={{ fontSize: 60 }} />}
                                    title={t('notifications.empty.title')}
                                    description={t('notifications.empty.description')}
                                />
                            ) : (
                                <>
                                    <List sx={{ mb: 3 }}>
                                        {notifications.map((notification) => (
                                            <Paper
                                                key={notification.id}
                                                elevation={0}
                                                sx={{
                                                    mb: 2,
                                                    borderLeft: notification.isRead ? '2px solid' : '4px solid',
                                                    borderColor: getNotificationColor(notification.type),
                                                    bgcolor: notification.isRead 
                                                        ? 'transparent' 
                                                        : 'rgba(92, 107, 255, 0.08)',
                                                    '&:hover': {
                                                        bgcolor: notification.isRead 
                                                            ? 'rgba(245, 245, 245, 0.02)' 
                                                            : 'rgba(123, 137, 255, 0.12)',
                                                        transform: notification.isRead 
                                                            ? 'none' 
                                                            : 'translateX(3px)',
                                                        boxShadow: notification.isRead 
                                                            ? 'none' 
                                                            : '0 2px 8px rgba(103, 58, 183, 0.15)'
                                                    },
                                                    transition: 'all 0.25s ease-out',
                                                    position: 'relative',
                                                    ...(!notification.isRead && {
                                                        '&::before': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: '50%',
                                                            height: '30%',
                                                            width: '2px',
                                                            background: `linear-gradient(
                                                                to bottom,
                                                                transparent 0%,
                                                                ${getNotificationColor(notification.type)} 50%,
                                                                transparent 100%
                                                            )`,
                                                            transform: 'translateY(-50%)',
                                                            opacity: 0.6
                                                        }
                                                    })
                                                }}
                                            >
                                                <ListItem
                                                    alignItems="flex-start"
                                                    onClick={() => handleNotificationClick(notification)}
                                                    sx={{
                                                        cursor: notification.link ? 'pointer' : 'default',
                                                        position: 'relative',
                                                        '&:hover': notification.link ? {
                                                            backgroundColor: 'action.hover'
                                                        } : {}
                                                    }}
                                                    secondaryAction={
                                                        <Box>
                                                            {!notification.isRead && (
                                                                <Tooltip title={t('notifications.markAsRead')}>
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label={t('notifications.markAsRead')}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleMarkAsRead(notification.id);
                                                                        }}
                                                                        sx={{ mr: 1 }}
                                                                    >
                                                                        <MarkAllReadIcon color="primary" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                            <Tooltip title={t('common.delete')}>
                                                                <IconButton
                                                                    edge="end"
                                                                    aria-label={t('common.delete')}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenDeleteDialog(notification);
                                                                    }}
                                                                >
                                                                    <DeleteIcon color="error" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    }
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: getNotificationColor(notification.type) }}>
                                                            {getNotificationIcon(notification.type)}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="subtitle1"
                                                                fontWeight={notification.isRead ? 'normal' : 'bold'}
                                                            >
                                                                {notification.title}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textPrimary"
                                                                    sx={{
                                                                        mb: 1,
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-word',
                                                                        a: {
                                                                            color: 'primary.main',
                                                                            textDecoration: 'underline',
                                                                            '&:hover': {
                                                                                textDecoration: 'none'
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {notification.message.split(/(https?:\/\/\S+)/g).map((part, index) => {
                                                                        if (part.match(/^https?:\/\/\S+$/)) {
                                                                            return (
                                                                                <MuiLink
                                                                                    key={index}
                                                                                    href={part}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    {part}
                                                                                </MuiLink>
                                                                            );
                                                                        }
                                                                        return part;
                                                                    })}
                                                                </Typography>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="textSecondary"
                                                                    >
                                                                        {formatRelativeTime(notification.createdAt)}
                                                                        <Tooltip title={formatFullDate(notification.createdAt)}>
                                                                            <span> (i)</span>
                                                                        </Tooltip>
                                                                    </Typography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={notification.isRead ? t('notifications.status.read') : t('notifications.status.unread')}
                                                                        color={notification.isRead ? 'default' : 'primary'}
                                                                        variant={notification.isRead ? 'outlined' : 'filled'}
                                                                    />
                                                                </Box>
                                                            </>
                                                        }
                                                    />
                                                </ListItem>
                                            </Paper>
                                        ))}
                                    </List>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            {t('notifications.pagination.count', { showing: notifications.length, total: totalCount })}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Button
                                                disabled={page === 1}
                                                onClick={() => handlePageChange(page - 1)}
                                            >
                                                {t('common.previous')}
                                            </Button>
                                            <Typography variant="body2" sx={{ mx: 2 }}>
                                                {t('common.pagination.page', { current: page, total: totalPages })}
                                            </Typography>
                                            <Button
                                                disabled={page === totalPages}
                                                onClick={() => handlePageChange(page + 1)}
                                            >
                                                {t('common.next')}
                                            </Button>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Paper>
                    <Dialog
                        open={deleteDialogOpen}
                        onClose={handleCloseDeleteDialog}
                        aria-labelledby="delete-dialog-title"
                        aria-describedby="delete-dialog-description"
                    >
                        <DialogTitle id="delete-dialog-title">
                            {t('notifications.delete.title')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="delete-dialog-description">
                                {t('notifications.delete.confirmation')}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDeleteDialog}>{t('common.cancel')}</Button>
                            <Button onClick={handleDeleteNotification} color="error" autoFocus>
                                {t('common.delete')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={deleteAllDialogOpen}
                        onClose={handleCloseDeleteAllDialog}
                        aria-labelledby="delete-all-dialog-title"
                        aria-describedby="delete-all-dialog-description"
                    >
                        <DialogTitle id="delete-all-dialog-title">
                            {t('notifications.deleteAll.title')}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="delete-all-dialog-description">
                                {t('notifications.deleteAll.confirmation')}
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDeleteAllDialog}>{t('common.cancel')}</Button>
                            <Button onClick={handleDeleteAllNotifications} color="error" autoFocus>
                                {t('common.deleteAll')}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Container>
    );
};

export default NotificationsPage;
